import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { desktopTheme as t } from '../styles/desktopTheme';

const NavContainer = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px 32px;
  background: ${t.bg};
  border-bottom: 1px solid ${t.borderSubtle};
  position: sticky;
  top: 0;
  z-index: 50;
`;

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1600px;
  justify-content: space-between;
`;

const AppHeaderRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 1600px;
  justify-content: space-between;
`;

const Tab = styled.button`
  background: none;
  border: none;
  padding: 8px 0;
  font-size: 14px;
  font-weight: ${props => props.active ? '500' : '400'};
  color: ${props => props.active ? t.textPrimary : 'rgba(255, 255, 255, 0.55)'};
  cursor: pointer;
  position: relative;
  transition: color 0.2s ease;

  &:hover {
    color: rgba(255, 255, 255, 0.82);
  }
`;

const HomeButton = styled.button`
  background: transparent;
  border: none;
  padding: 8px 0;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.75);
  cursor: pointer;

  &:hover {
    color: ${t.textPrimary};
  }
`;

const RightControls = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const IconButton = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid ${t.borderSubtle};
  background: rgba(255, 255, 255, 0.02);
  color: rgba(255, 255, 255, 0.82);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: ${t.borderStrong};
  }
`;

const Glyph = styled.svg`
  width: 16px;
  height: 16px;
  stroke: currentColor;
  fill: none;
  stroke-width: 1.6;
  stroke-linecap: round;
  stroke-linejoin: round;
`;

const MAIN_TABS = [
  { id: 'dashboard', label: 'Dashboard', path: '/desktop-dashboard' },
  { id: 'chat', label: 'Chat', path: '/manager' },
  { id: 'timeline', label: 'Timeline', path: '/activity-feed' },
];

const APP_LABELS = {
  catalog: 'Catalog',
  bookings: 'Bookings',
  'tour-manager': 'Tour Manager',
  tasks: 'Tasks',
  contacts: 'Contacts',
  notes: 'Notes',
  flows: 'Workflows',
  analytics: 'Analytics',
  data: 'Data',
  merch: 'Merch',
  email: 'Email',
  'knowledge-base': 'Knowledge',
};

const APP_ACTIONS = {
  // These apps have existing in-page Search/Menu toggles we can drive from the top header.
  catalog: { search: true, menu: true },
  bookings: { search: true, menu: true },
};

const AppTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${t.textPrimary};
  letter-spacing: 0.2px;
`;

export default function DesktopTopNav({ activeApp }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Check which tab is active
  const getActiveTab = () => {
    const path = location.pathname;

    // Global tabs
    for (const tab of MAIN_TABS) {
      if (path === tab.path || path.startsWith(tab.path)) {
        return tab.id;
      }
    }
    
    // Default to dashboard
    if (path === '/' || path === '/desktop-dashboard') {
      return 'dashboard';
    }
    
    return null;
  };

  const activeTabId = getActiveTab();

  const isAppMode = !!(activeApp && !['chat', 'timeline', 'dashboard'].includes(activeApp));
  const appLabel = (activeApp && APP_LABELS[activeApp]) || 'App';
  const appActions = (activeApp && APP_ACTIONS[activeApp]) || {};

  const fireAppAction = (action) => {
    if (!activeApp) return;
    window.dispatchEvent(
      new CustomEvent('desktopTopNavAction', { detail: { app: activeApp, action } })
    );
  };

  return (
    <NavContainer>
      {isAppMode ? (
        <AppHeaderRow>
          <AppTitle>{appLabel}</AppTitle>
          <RightControls>
            <IconButton onClick={() => navigate(-1)} aria-label="Back">
              <Glyph viewBox="0 0 24 24">
                <path d="M15 18l-6-6 6-6" />
              </Glyph>
            </IconButton>
            {appActions.search ? (
              <IconButton onClick={() => fireAppAction('search')} aria-label="Search">
                <Glyph viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="6" />
                  <path d="M20 20l-3.5-3.5" />
                </Glyph>
              </IconButton>
            ) : null}
            {appActions.menu ? (
              <IconButton onClick={() => fireAppAction('menu')} aria-label="Menu">
                <Glyph viewBox="0 0 24 24">
                  <path d="M5 7h14" />
                  <path d="M5 12h14" />
                  <path d="M5 17h14" />
                </Glyph>
              </IconButton>
            ) : null}
            <HomeButton onClick={() => navigate('/desktop-dashboard')}>HOME</HomeButton>
          </RightControls>
        </AppHeaderRow>
      ) : (
        <TabsContainer>
          {MAIN_TABS.map((tab) => (
            <Tab
              key={tab.id}
              active={activeTabId === tab.id}
              onClick={() => navigate(tab.path)}
            >
              {tab.label}
            </Tab>
          ))}
        </TabsContainer>
      )}
    </NavContainer>
  );
}

